//@flow
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import DataUtils from 'common/utils/DataUtils';
import { SalesCategoryId } from 'common/models/internal/SalesCategoryId';
import * as Immutable from 'immutable';
import { Revenue } from 'common/models/internal/Revenue';
import I18nHelper from 'common/helpers/I18nHelper';
import { MasterRider, RiderTypeType } from 'common/models/internal/MasterRider';
import PjpImportHelper from 'common/entity/helpers/PjpImportHelper';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import getSymbolFromCurrency from 'currency-symbol-map';
import { RiderPersonaId } from 'common/models/internal/RiderPersonaId';
import { EntitySettings } from 'common/models/internal/EntitySettings';
import StringUtils from 'common/utils/StringUtils';
import { DateTimeFormat } from 'common/utils/TimeUtils';
import DateTimeUtils, { CustomDateFormats } from 'common/utils/DateTimeUtils';

const translationsNamespace = ['validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

const processDateValues = ({ cellValue, localeSettings }) => {
    const isDateFormatMMDDYYYY = localeSettings.isDateFormatMMDDYYYY();
    const inputFormat = isDateFormatMMDDYYYY
        ? [
              DateTimeFormat.SHEET_DATE_FORMAT_YYYY_MONTH_FIRST,
              DateTimeFormat.SHEET_DATE_FORMAT_MONTH_FIRST
          ]
        : [
              DateTimeFormat.SHEET_DATE_FORMAT_YYYY,
              DateTimeFormat.SHEET_DATE_FORMAT
          ];
    return (
        DateTimeUtils.customFormat(
            cellValue,
            CustomDateFormats.API_DATE,
            inputFormat
        ) || cellValue
    );
};

export class RiderImportHelper extends EntityImportDataHelper {
    static processJoiningDate({ cellValue, localeSettings }) {
        return processDateValues({ cellValue, localeSettings });
    }

    static processDateOfBirth({ cellValue, localeSettings }) {
        return processDateValues({ cellValue, localeSettings });
    }

    static processSalesCategoryIds({ cellValue, clientId }) {
        return !DataUtils.isEmpty(cellValue)
            ? cellValue
                  .split(',')
                  .filter(id => id && id.trim())
                  .map(id => {
                      return {
                          salesCategoryId: id.trim(),
                          clientId
                      };
                  })
            : [];
    }

    static processZones({ cellValue }) {
        return !DataUtils.isEmpty(cellValue)
            ? Immutable.List(
                  cellValue
                      .split(',')
                      .filter(zone => zone)
                      .map(zone => {
                          return zone.trim();
                      })
              )
            : Immutable.List([]);
    }

    static validateSalesCategories({ entity }) {
        const salesCategories = entity.pjpData.salesCategoryIds;
        const [, duplicateCategoryIds] = salesCategories.reduce(
            ([duplicateCategories, duplicateCategoryIds], category) => {
                let nextDuplicateCategoryIds = duplicateCategoryIds;
                duplicateCategories[category.salesCategoryId] =
                    duplicateCategories[category.salesCategoryId] + 1 || 0;
                if (duplicateCategories[category.salesCategoryId]) {
                    nextDuplicateCategoryIds = nextDuplicateCategoryIds.length
                        ? `${nextDuplicateCategoryIds}, ${category.salesCategoryId}`
                        : category.salesCategoryId;
                }
                return [duplicateCategories, nextDuplicateCategoryIds];
            },
            [{}, '']
        );
        if (duplicateCategoryIds) {
            return nsTranslate('duplicateSalesCategories', {
                salesCategories: duplicateCategoryIds
            });
        }
    }

    static processRiderPersonaId({ cellValue, clientId }) {
        return cellValue
            ? {
                  riderPersonaId: cellValue.trim(),
                  clientId
              }
            : {};
    }

    static serializeRiderPersonaId = (RiderPersona: {
        clientId: string,
        riderPersonaId: string
    }): RiderPersonaId => RiderPersonaId.fromJS(RiderPersona);

    static processPjpRevenue({
        cellValue,
        revenueMonthCount,
        defaultCurrency,
        symbol
    }) {
        return {
            numberOfMonths: revenueMonthCount,
            amount: {
                amount: cellValue,
                currency: defaultCurrency,
                symbol
            }
        };
    }

    static validateWorkingDaysLocation({ entity, header }) {
        const dayOfWeek = header.accessor.get(2); // day of week
        const locationType = header.accessor.get(3); // start or end
        const latLngType = header.getFieldAccessor(); // lat or lng
        const { workingDays } = entity.shiftsInfo;
        const workingDay = workingDays.find(
            workingDay => workingDay.dayOfWeek === dayOfWeek
        );
        return (
            workingDay &&
            workingDay.getIn([locationType, 'latLng']) &&
            workingDay
                .getIn([locationType, 'latLng'])
                .getValidationMessage(latLngType)
        );
    }

    static validateWorkingDaysBreak({ entity, header }) {
        const [dayOfWeek] = header.id.split('.');
        const { workingDays } = entity.shiftsInfo;
        const workingDay = workingDays.find(
            workingDay => workingDay.dayOfWeek === dayOfWeek
        );
        return workingDay && workingDay.getValidationMessage('breaks');
    }

    static validateWorkingDaysTimeSlot({ entity, header }) {
        const [dayOfWeek] = header.id.split('.');
        const { workingDays } = entity.shiftsInfo;
        const workingDay = workingDays.find(
            workingDay => workingDay.dayOfWeek === dayOfWeek
        );
        return workingDay && workingDay.getValidationMessage('slot');
    }

    static getWorkingDaysAccessor = accessor =>
        EntityImportDataHelper.getTimeSlotsAccessor({
            ...accessor,
            workingDaysAccessor: ['shiftsInfo', 'workingDays'],
            dayOfWeekIndex: 2
        });

    /**
     * This method takes `salesCategoryIds` object and @returns `SalesCategoryId` object of `Immutable.List`
     * Used for edit masters table
     * @static
     * @memberof RiderImportHelper
     */
    static serializeSalesCategoryIds = (
        salesCategoryIds: Array
    ): Immutable.List =>
        Immutable.List(salesCategoryIds).map(item =>
            SalesCategoryId.fromJS(item)
        );

    /**
     * This method takes `revenue` object and @returns `Revenue` object of `Immutable.List`
     * Used for edit masters table
     * @static
     * @memberof RiderImportHelper
     */
    static serializeRevenue = (revenue: Object): Revenue =>
        Revenue.fromJS(revenue);

    static getCustomFields(entitySettings) {
        return entitySettings && entitySettings.riderSettings.customFields;
    }

    static sanitizeWorkingDays(workingDays) {
        return workingDays.reduce((currentWorkingDays, dayShift, dayOfWeek) => {
            let newWorkingDays = currentWorkingDays;
            newWorkingDays = newWorkingDays.push(
                dayShift.set('dayOfWeek', dayOfWeek)
            );
            return newWorkingDays;
        }, Immutable.List());
    }

    static processEntityRows = ({ rows }, { getDataForCell }) => {
        let workingDays;
        let dateOfBirth;
        let joiningDate;
        let updatedSlots;
        const { salesChannels, entitySettings } = getDataForCell();
        return rows.map(entityRow => {
            let updatedRiderRow = entityRow.map((cellValue, headerId) => {
                switch (headerId) {
                    case 'shiftsInfo':
                        workingDays = cellValue.get('workingDays');
                        updatedSlots =
                            workingDays &&
                            RiderImportHelper.sanitizeWorkingDays(workingDays);

                        if (updatedSlots?.size) {
                            return cellValue.set('workingDays', updatedSlots);
                        }
                        return cellValue.set('workingDays', null);
                    case 'bioData':
                        dateOfBirth = cellValue.get('dateOfBirth');
                        if (!dateOfBirth) {
                            return cellValue.set('dateOfBirth', null);
                        }
                        return cellValue;
                    case 'competency':
                        joiningDate = cellValue.get('joiningDate');
                        if (!joiningDate) {
                            return cellValue.set('joiningDate', null);
                        }
                        return cellValue;
                    case 'customProperties':
                        return cellValue.filter(value => value);
                    case 'pjpData': {
                        const nextChannels = PjpImportHelper.serializeChannels(
                            cellValue.get('channels'),
                            {
                                salesChannels
                            }
                        );
                        return cellValue.set('channels', nextChannels);
                    }
                    default:
                        return cellValue;
                }
            });
            /* When riderPersonaId is disabled and imported file doesn't have riderPersonaId,
            validateRiderPersonaId in InternalMasterRider will throw an error saying riderPersonaId is empty. This is
            to skip that validation */

            if (
                RiderImportHelper.isFieldHidden({
                    property: 'riderPersona',
                    entitySettings
                })
            ) {
                updatedRiderRow = updatedRiderRow.set(
                    'shouldSkipRiderPersonaIdValidation',
                    true
                );
            }
            if (
                RiderImportHelper.isFieldHidden({
                    property: 'pjp',
                    entitySettings
                })
            ) {
                updatedRiderRow = updatedRiderRow.setIn(
                    ['pjpData', 'shouldSkipSalesCategoryIdsValidation'],
                    true
                );
                updatedRiderRow = updatedRiderRow.set(
                    'type',
                    RiderTypeType.Rider
                );
            }

            const processedRow = MasterRider.fromJS(updatedRiderRow.toJS());

            //skip team field validation for import flow
            return processedRow.setShouldSkipTeamsValidation(true);
        });
    };

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }) => {
        const ridersData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: (rowsData, otherProps) =>
                RiderImportHelper.processEntityRows(rowsData, {
                    ...otherProps,
                    getDataForCell
                })
        });
        const riderEntities = Immutable.List(ridersData);

        return riderEntities;
    };

    static getRevenueMonthCount = ({ entitySettings }) =>
        entitySettings?.riderSettings.revenueMonthCount;

    static getDataForCell = ({
        clientId,
        accessor,
        entitySettings,
        localeSettings
    }) => {
        const { currency: defaultCurrency } = localeSettings;
        const salesChannels = RiderImportHelper.selectSalesChannelsFromEntitySettings(
            entitySettings
        );
        const revenueMonthCount = RiderImportHelper.getRevenueMonthCount({
            entitySettings
        });
        if (accessor?.get(0) === 'pjpData') {
            return {
                revenueMonthCount,
                symbol: getSymbolFromCurrency(defaultCurrency),
                currency: defaultCurrency,
                clientId,
                salesChannels
            };
        }
        return {
            entitySettings,
            salesChannels,
            clientId,
            localeSettings
        };
    };

    static generateEntityList = (
        ridersData: Array<*>
    ): Immutable.List<MasterRider> =>
        Immutable.List(
            ridersData.map(riderData => MasterRider.fromJS(riderData))
        );

    static validateEmail = ({ entity, entitySettings }) => {
        const email = entity.bioData?.emailAddress?.emailAddress;
        const isEmailRequired =
            entitySettings?.riderSettings.enforceEmailUniqueness;
        if (email) {
            const isValidEmail = StringUtils.isEmail(email);

            if (!isValidEmail) {
                return nsTranslate('settings:errorMessage.invalidEmailFormat');
            }
        } else if (isEmailRequired) {
            return nsTranslate(
                'entityManagement:riderValidationErrors.emailEmpty'
            );
        }
    };

    /**
     * FIXME - duplicate of RiderMasterContainer>areFieldsHidden
     * Find out common place to keep helper for master container, import and single add/edit
     */
    static isFieldHidden = ({
        property,
        entitySettings
    }: {
        property: string,
        entitySettings: EntitySettings
    }): boolean => {
        switch (property) {
            case 'riderPersona':
                return !entitySettings.riderPersonaSettings.enabled;
            case 'pjp':
                return !entitySettings.pjpSettings.enabled;
            default:
                return false;
        }
    };
}

export default RiderImportHelper;
